import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Button, PageHeader, ListPage } from '@/components/ui';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { describeActionError } from '@/utils/loadError';
import { Effort } from '../../../types/Effort';
import { RoutineTask } from '../../../types/RoutineTask';
import { routineTaskService, RoutineTaskListResponse, RoutineTaskFilters } from '../../../services/routineTaskService';
import RoutineWorkTable from '../components/RoutineWorkTable';
import RoutineTaskModal from '../../../components/RoutineTaskModal';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import RoutineTaskFiltersComponent from '../components/RoutineTaskFilters';
import ServerPagination from '../../../components/ServerPagination';
import { ForemanArea } from '../../../types/ForemanArea';
import { Activity } from '../../../types/Activity';
import { activityService } from '../../../services/activityService';
import TaskHistoryModal from '../../../components/TaskHistoryModal';
import { Task } from '../../../types/Task';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { taskService } from '../../../services/taskService';
import CloneTaskConfirmModal from '../../../components/CloneTaskConfirmModal';
import PrintTaskPDFModal from '../../quantified-tracking/components/PrintTaskPDFModal';
import { scrollListToTopAfterPaint } from '../../../utils/scrollListToTop';
import {
  applyRoutineFiltersToSearchParams,
  parseRoutineFiltersFromSearchParams,
} from '../utils/routineFilterUrlParams';
import {
  nextRoutineSortOrder,
  parseRoutineSort,
  ROUTINE_SERVER_SORT_FIELDS,
} from '../utils/routineSort';
import { useTasksView } from '../../tasks/TasksPage';

// routineTaskService.updateTaskStatus uses fetch and throws a plain Error carrying the API's own
// `error` text (e.g. why a cancel is blocked). Show that text; fall back to the generic message
// when the service only had a status code to report.
function routineStatusError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message && !error.message.startsWith('Failed to update task status')) {
    return error.message;
  }
  return describeActionError(error, fallback);
}

const RoutineWorkPage: React.FC = () => {
  // V2-036: inside the Tasks page this list is a view – header "Tasks", switcher under it.
  const tasksView = useTasksView();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<RoutineTask[]>([]);
  const [loading, setLoading] = useState(false);
  // A failed row action (close, reopen, cancel, clone…) is shown inline above the list (STATE-06).
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<RoutineTask | undefined>(undefined);
  const mergeRoutineVehicleFuelEffort = useCallback((effort: Effort) => {
    if (effort.taskId == null) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id !== effort.taskId && t.taskId !== effort.taskId
          ? t
          : {
              ...t,
              efforts: (t.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
            }
      )
    );
    setSelectedTask((prev) =>
      prev && (prev.id === effort.taskId || prev.taskId === effort.taskId)
        ? {
            ...prev,
            efforts: (prev.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
          }
        : prev
    );
  }, []);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [printPdfTask, setPrintPdfTask] = useState<Task | null>(null);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<Task | null>(null);
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canCreateTask = hasPermission('routine:specific_create');
  const canCloneTask =
    canCreateTask ||
    hasPermission('project:task_create') ||
    hasPermission('project:task_edit');
  const canReopenTask = hasPermission('project:task_close');
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  // Add separate search states
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [cloneConfirmRoutine, setCloneConfirmRoutine] = useState<RoutineTask | null>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const pendingScrollAfterCreateRef = useRef(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const initialSort = parseRoutineSort(searchParams);
  const [sortBy, setSortBy] = useState<string | null>(initialSort.sortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSort.sortOrder);
  const [filters, setFilters] = useState<RoutineTaskFilters>({
    status: [],
    foremanArea: [],
    activityNumber: [],
    priority: '',
    startDate: '',
    endDate: '',
    search: '',
    serviceArea: ''
  });
  const [pendingFilters, setPendingFilters] = useState<RoutineTaskFilters>({
    status: [],
    foremanArea: [],
    activityNumber: [],
    priority: '',
    startDate: '',
    endDate: '',
    search: '',
    serviceArea: ''
  });
  const [filterOptions, setFilterOptions] = useState<{
    statuses: string[];
    priorities: string[];
    foremanAreas: Array<{ id: number; name: string; }>;
    activities: Array<{ id: number; activityName: string; activityNumber: string; }>;
    serviceAreas: Array<{ id: number; name: string; }>;
  }>({
    statuses: [],
    priorities: [],
    foremanAreas: [],
    activities: [],
    serviceAreas: []
  });

  /**
   * The list state lives in the URL here. To keep it for the sign-in (V2-024, FLT-09) the query
   * string - filters, search, sort, page - is remembered, and opening the page with a bare URL
   * (the nav link) puts it back. Deep links (taskId, returnUrl) are not remembered.
   */
  const [savedQuery, setSavedQuery] = usePersistedListState('routine', 'query', '');
  const restoringRef = useRef(searchParams.toString() === '' && savedQuery !== '');

  // Initialize state from URL parameters
  useEffect(() => {
    if (restoringRef.current) {
      if (searchParams.toString() === '') {
        setSearchParams(new URLSearchParams(savedQuery), { replace: true });
        return;
      }
      restoringRef.current = false;
    }
    const remembered = new URLSearchParams(searchParams);
    remembered.delete('taskId');
    remembered.delete('returnUrl');
    setSavedQuery(remembered.toString());

    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize') || '20');
    
    const filtersFromUrl = parseRoutineFiltersFromSearchParams(searchParams);
    const sortFromUrl = parseRoutineSort(searchParams);
    
    setPagination(prev => ({ ...prev, page: pageFromUrl, pageSize: pageSizeFromUrl }));
    setSortBy(sortFromUrl.sortBy);
    setSortOrder(sortFromUrl.sortOrder);
    setFilters(filtersFromUrl);
    setPendingFilters(filtersFromUrl);
    // Initialize search states from URL
    setSearchTerm(filtersFromUrl.search || '');
    setSearchInput(filtersFromUrl.search || '');
  }, [searchParams]);

  const taskIdFromUrl = searchParams.get('taskId');
  const returnUrl = searchParams.get('returnUrl') || '';
  const isFromWorkIds = returnUrl === '/work-ids';

  // Fetch tasks when pagination or filters change.
  // When taskId is in URL, include it in search so the task appears in the list (API searches by id).
  useEffect(() => {
    // Wait for the remembered query to land in the URL rather than fetch the defaults first.
    if (restoringRef.current) return;
    fetchTasks();
  }, [pagination.page, pagination.pageSize, filters, searchTerm, taskIdFromUrl, sortBy, sortOrder]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Open task modal when taskId is in URL and the task appears in the list.
  // We include taskId in the search (above) so it's fetched; then we open from list results.
  useEffect(() => {
    if (!taskIdFromUrl || tasks.length === 0) return;
    const taskId = parseInt(taskIdFromUrl, 10);
    if (isNaN(taskId)) return;
    const task = tasks.find((t) => t.id === taskId || (t as { taskId?: number }).taskId === taskId);
    if (task) {
      setSelectedTask(task);
      setIsModalVisible(true);
    } else {
      // Fallback: try direct API when task not in current page
      let cancelled = false;
      routineTaskService.getTaskDetails(taskId).then((t) => {
        if (cancelled) return;
        setSelectedTask(t);
        setIsModalVisible(true);
      }).catch((err) => {
        if (!cancelled) console.error('Failed to load task for taskId:', taskId, err);
      });
      return () => { cancelled = true; };
    }
  }, [taskIdFromUrl, tasks]);

  const updateUrlParams = (newPagination?: Partial<typeof pagination>, newFilters?: RoutineTaskFilters) => {
    const params = new URLSearchParams(searchParams);
    const preserveTaskId = searchParams.get('taskId');

    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }

    if (newFilters) {
      applyRoutineFiltersToSearchParams(params, newFilters);
    }

    if (preserveTaskId) params.set('taskId', preserveTaskId);
    setSearchParams(params);
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await routineTaskService.getRoutineTaskFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchTasks = async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    try {
      setLoading(true);
      // When taskId is in URL, filter to only that task and use page 1
      const filterByTaskId = taskIdFromUrl && /^\d+$/.test(taskIdFromUrl);
      const search = filterByTaskId ? (searchTerm || taskIdFromUrl) : searchTerm;
      const combinedFilters = {
        ...filters,
        search,
        ...(filterByTaskId && taskIdFromUrl ? { taskId: taskIdFromUrl } : {})
      };
      const pageToUse = filterByTaskId ? 1 : pagination.page;
      const serverSort =
        sortBy && ROUTINE_SERVER_SORT_FIELDS.has(sortBy)
          ? { sortBy, sortOrder }
          : undefined;
      const response: RoutineTaskListResponse = await routineTaskService.getRoutineTasksPaginated(
        pageToUse,
        pagination.pageSize,
        combinedFilters,
        'specific',
        serverSort
      );

      if (requestId !== requestIdRef.current) return;

      setTasks(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));

      if (pendingScrollAfterCreateRef.current) {
        pendingScrollAfterCreateRef.current = false;
        scrollListToTopAfterPaint(listScrollRef.current);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching tasks:', error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const executeRoutineClone = async (task: RoutineTask, nameOverride?: string) => {
    const taskId = task.taskId ?? task.id;
    try {
      setActionError(null);
      setLoading(true);
      const cloned = await taskService.cloneTask(
        taskId,
        nameOverride != null && nameOverride.trim() !== '' ? { name: nameOverride.trim() } : undefined
      );
      const needle = (cloned.taskNumber || String(cloned.id)).trim();
      const params = new URLSearchParams(searchParams);
      params.set('taskId', String(cloned.id));
      params.set('search', needle);
      params.set('page', '1');
      setSearchParams(params);
    } catch (error) {
      console.error('Error cloning task:', error);
      setActionError(describeActionError(error, "Couldn't clone the task. Try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleTaskAction = async (action: string, task: RoutineTask) => {
    console.log('RoutineWorkPage - handleTaskAction:', { action, taskId: task.id });
    
    switch (action) {
      case 'edit':
        setSelectedTask(task);
        setIsModalVisible(true);
        break;
        
      case 'print-pdf':
        handlePrintTaskPDF(task);
        break;
        
      case 'close':
        try {
          setActionError(null);
          setLoading(true);
          const taskId = task.taskId ?? task.id;
          await routineTaskService.updateTaskStatus(taskId, 'CLOSED', 'Closed from Routine Work page');
          await fetchTasks(); // Reload tasks to get updated data
        } catch (error) {
          console.error('Error closing task:', error);
          setActionError(routineStatusError(error, "Couldn't close the task. Try again."));
        } finally {
          setLoading(false);
        }
        break;
        
      case 'reopen':
        try {
          setActionError(null);
          setLoading(true);
          const taskId = task.taskId ?? task.id;
          await routineTaskService.updateTaskStatus(taskId, 'ACTIVE', 'Reopened from Routine Work page');
          await fetchTasks(); // Reload tasks to get updated data
        } catch (error) {
          console.error('Error reopening task:', error);
          setActionError(routineStatusError(error, "Couldn't reopen the task. Try again."));
        } finally {
          setLoading(false);
        }
        break;
        
      case 'cancel':
        try {
          setActionError(null);
          setLoading(true);
          const taskId = task.taskId ?? task.id;
          await routineTaskService.updateTaskStatus(taskId, 'CANCELLED');
          await fetchTasks(); // Reload tasks to get updated data
        } catch (error: unknown) {
          console.error('Error cancelling task:', error);
          // Surface the server's reason (e.g. a linked Work ID blocks the cancel).
          setActionError(routineStatusError(error, "Couldn't cancel the task. Try again."));
        } finally {
          setLoading(false);
        }
        break;

      case 'clone-task':
        setCloneConfirmRoutine(task);
        break;

      case 'history':
        // Convert RoutineTask to Task format for the modal
        const taskForHistory: Task = {
          id: task.id,
          type: task.type as any,
          name: task.name || '',
          description: task.description || '',
          status: task.status as any,
          taskHistory: task.taskHistory || [],
          createdAt: task.createdAt ? new Date(task.createdAt) : new Date(),
          updatedAt: new Date(),
          efforts: task.efforts || [],
          activities: task.activities || [],
          taskLocations: task.taskLocations || [],
          foremanArea: task.foremanArea || undefined,
          project: task.project || undefined
        } as Task;
        setSelectedTaskForHistory(taskForHistory);
        setIsHistoryModalVisible(true);
        break;
        
      default:
        console.warn('Unknown action:', action);
    }
  };

  const handleBulkAction = (action: string, tasks: RoutineTask[]) => {
    console.log('Bulk action:', action, tasks);
  };

  const handleTaskClick = (task: RoutineTask) => {
    setSelectedTask(task);
    setIsModalVisible(true);
  };

  const handleTaskSelect = (taskIds: number[]) => {
    setSelectedTasks(taskIds);
  };

  const handleModalClose = () => {
    setSelectedTask(undefined);
    setIsModalVisible(false);
    // Keep taskId in URL so the filter stays applied when modal is closed
  };

  const handleCreateTask = () => {
    setSelectedTask(undefined);
    setIsModalVisible(true);
  };

  const handleTaskSaved = async (
    updatedTask: RoutineTask,
    options?: { keepOpen?: boolean }
  ) => {
    if (options?.keepOpen) {
      setSelectedTask(updatedTask);
      return;
    }
    const isNew = !selectedTask;
    setIsModalVisible(false);
    if (isNew) {
      pendingScrollAfterCreateRef.current = true;
      const urlPage = parseInt(searchParams.get('page') || '1', 10);
      if (urlPage !== 1 || pagination.page !== 1) {
        updateUrlParams({ page: 1 }, filters);
      } else {
        await fetchTasks();
      }
    } else {
      await fetchTasks();
    }
  };

  const handleAddHistoryComment = async (updatedTask: Task) => {
    // Update the task in the local state
    setTasks(prevTasks => 
      prevTasks.map(task => {
        if (task.id === updatedTask.id) {
          return {
            ...task,
            taskHistory: updatedTask.taskHistory || []
          };
        }
        return task;
      })
    );
    // Update the selected task for history if it's the same one
    if (selectedTaskForHistory && selectedTaskForHistory.id === updatedTask.id) {
      setSelectedTaskForHistory(updatedTask);
    }
  };

  const handlePrintTaskPDF = (task: RoutineTask) => {
    setPrintPdfTask(task as unknown as Task);
  };

  const handleFilterChange = (name: string, value: string | string[]) => {
    const newPendingFilters = { ...pendingFilters, [name]: value };
    setPendingFilters(newPendingFilters);
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    updateUrlParams({ page: 1 }, pendingFilters); // Reset to page 1 when applying filters
  };

  // Search lives in the filter panel header (V2-077): Enter (or the clear "x") runs it, within the
  // applied filters (V2-076).
  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchTerm(trimmed);
    // Update URL params with trimmed search term
    const newFilters = { ...filters, search: trimmed };
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handleSort = (field: string) => {
    const nextOrder = nextRoutineSortOrder(sortBy, sortOrder, field);
    const params = new URLSearchParams(searchParams);
    params.set('sortBy', field);
    params.set('sortOrder', nextOrder);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const newPagination = { ...pagination, page };
    setPagination(newPagination);
    updateUrlParams({ page }, filters);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const newPagination = { ...pagination, pageSize, page: 1 };
    setPagination(newPagination);
    updateUrlParams({ pageSize, page: 1 }, filters);
  };

  const handleClearFilters = () => {
    const clearedFilters: RoutineTaskFilters = {
      status: [],
      foremanArea: [],
      activityNumber: [],
      activityName: [],
      priority: '',
      startDate: '',
      endDate: '',
      search: '',
      serviceArea: ''
    };
    setPendingFilters(clearedFilters);
    setFilters(clearedFilters);
    updateUrlParams({ page: 1 }, clearedFilters);
  };

  // Check if there are any active filters
  const hasActiveFilters = () => {
    return Object.entries(filters).some(([key, value]) => {
      // Don't count search as a filter since it's handled separately
      if (key === 'search') return false;
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '';
    });
  };

  /** How many applied filters differ from their defaults - the collapsed filter bar's chip (W-iP6). */
  const activeFilterCount = () =>
    Object.entries(filters).filter(([key, value]) => {
      if (key === 'search' || key === 'taskId') return false;
      return Array.isArray(value) ? value.length > 0 : !!value;
    }).length;

  // Check if pending filters are different from applied filters
  const hasUnappliedChanges = () => {
    const compareArrays = (a: string | string[] | undefined, b: string | string[] | undefined): boolean => {
      const arrA = Array.isArray(a) ? a : (a ? [a] : []);
      const arrB = Array.isArray(b) ? b : (b ? [b] : []);
      return arrA.length !== arrB.length || !arrA.every((v, i) => v === arrB[i]);
    };
    
    return compareArrays(pendingFilters.status, filters.status) ||
           compareArrays(pendingFilters.foremanArea, filters.foremanArea) ||
           compareArrays(pendingFilters.activityNumber, filters.activityNumber) ||
           compareArrays(pendingFilters.activityName, filters.activityName) ||
           pendingFilters.priority !== filters.priority ||
           pendingFilters.startDate !== filters.startDate ||
           pendingFilters.endDate !== filters.endDate ||
           pendingFilters.serviceArea !== filters.serviceArea;
  };

  const toggleRowExpansion = (taskId: number) => {
    setExpandedRows(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  return (
    // ListPage owns the page padding and the fixed-height column; the table box under the filters
    // is the one vertical scroll area (W-H5). The old tw-p-4 / h-full / overflow-hidden wrapper and
    // the bottom spacer divs are gone.
    <ListPage className="routine-work-page">
      {/* Back link sits above the title as a breadcrumb rather than competing with it inline. */}
      {isFromWorkIds && (
        <div className="tw-mb-2">
          <Button
            as={Link}
            to={returnUrl}
            variant="link"
            size="sm"
            className="tw-p-0"
            title="Return to Work IDs"
          >
            <i className="bi bi-arrow-left tw-mr-1" aria-hidden />
            Back to Work IDs
          </Button>
        </div>
      )}
      <PageHeader
        title={tasksView ? 'Tasks' : 'Routine Work'}
        viewToggle={tasksView?.switcher}
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => {
              setLoading(true);
              Promise.all([
                fetchTasks(),
                fetchFilterOptions()
              ]).finally(() => setLoading(false));
            },
          },
          {
            key: 'create',
            label: 'Add Routine Task',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateTask,
            title: 'Create new routine task',
            onClick: handleCreateTask,
          },
        ]}
      />

      <RoutineTaskFiltersComponent
        filterOptions={filterOptions}
        filters={pendingFilters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        showApplyButton={true}
        onApply={handleApplyFilters}
        applyDisabled={!hasUnappliedChanges()}
        hasActiveFilters={hasActiveFilters()}
        activeFilterCount={activeFilterCount()}
        // No filter hides routine tasks by default, so the applied count is the changed count.
        appliedFilterCount={activeFilterCount()}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Task ID, description, asset or activity',
          label: 'Search routine tasks',
        }}
      />

      {actionError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setActionError(null)} className="tw-mb-2">
          {actionError}
        </Alert>
      )}

      {/* The table takes all the height left under the filters. The bulk bar, the loading spinner
          and the table box are always rendered, so selecting a row or refetching never moves the
          list (W-L7). */}
      <ListPage.Body ref={listScrollRef} className="tw-mt-1">
        <RoutineWorkTable
          tasks={tasks}
          loading={loading}
          onTaskAction={handleTaskAction}
          onBulkAction={handleBulkAction}
          onTaskClick={handleTaskClick}
          onTaskSelect={handleTaskSelect}
          selectedTasks={selectedTasks}
          showAsset={true}
          showQuantity={true}
          onVehicleFuelEffortPatched={mergeRoutineVehicleFuelEffort}
          canCloneTask={canCloneTask}
          sortField={sortBy}
          sortDirection={sortOrder}
          onSort={handleSort}
          serverSorted={sortBy != null && ROUTINE_SERVER_SORT_FIELDS.has(sortBy)}
        />
        {selectedTask && (
          <div className="tw-mt-4 tw-shrink-0">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <h3 className="tw-text-xl tw-mb-0">
                Task Efforts - {selectedTask.taskNumber}
                <span className="tw-text-content-muted tw-ml-2">{selectedTask.description}</span>
              </h3>
              {selectedTask.efforts && selectedTask.efforts.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  active={expandedRows.includes(selectedTask.id)}
                  onClick={() => toggleRowExpansion(selectedTask.id)}
                  title={expandedRows.includes(selectedTask.id) ? "Hide Efforts" : "Show Efforts"}
                >
                  <i className={`bi bi-chevron-${expandedRows.includes(selectedTask.id) ? 'up' : 'down'}`}></i>
                </Button>
              )}
            </div>
            {expandedRows.includes(selectedTask.id) && (
              <div className="tw-p-3 tw-bg-surface-alt tw-border tw-border-line tw-rounded">
                <TaskEffortsTable 
                  efforts={selectedTask.efforts || []}
                  columns={{
                    type: true,
                    date: true,
                    employeeName: true,
                    description: true,
                    accomplishment: true,
                    quantity: true,
                    rate: true,
                    fuelSurcharge: true,
                    totalCost: true,
                    comments: true
                  }}
                  canToggleVehicleFuel={canToggleVehicleFuel}
                  onVehicleFuelEffortPatched={mergeRoutineVehicleFuelEffort}
                />
              </div>
            )}
          </div>
        )}
      </ListPage.Body>

      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-2"
      />

      <RoutineTaskModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onTaskSaved={handleTaskSaved}
        task={selectedTask}
      />

      <CloneTaskConfirmModal
        show={cloneConfirmRoutine !== null}
        onHide={() => setCloneConfirmRoutine(null)}
        onConfirm={(names) => {
          const t = cloneConfirmRoutine;
          setCloneConfirmRoutine(null);
          if (t) void executeRoutineClone(t, names[0]);
        }}
        items={
          cloneConfirmRoutine
            ? [
                {
                  taskId: cloneConfirmRoutine.id,
                  sourceTaskLabel: cloneConfirmRoutine.taskNumber || `#${cloneConfirmRoutine.id}`,
                  sourceTaskName: cloneConfirmRoutine.name
                }
              ]
            : []
        }
      />

      {selectedTaskForHistory && (
        <TaskHistoryModal
          visible={isHistoryModalVisible}
          onClose={() => {
            setIsHistoryModalVisible(false);
            setSelectedTaskForHistory(null);
          }}
          task={selectedTaskForHistory}
          onAddComment={handleAddHistoryComment}
        />
      )}

      <PrintTaskPDFModal
        task={printPdfTask}
        onClose={() => setPrintPdfTask(null)}
      />
    </ListPage>
  );
};

export default RoutineWorkPage; 